<template>
  <div class="w-full h-full dark:bg-dark-box-background">
    <div class="w-full mb-2">
      <TextInput
        :value="template_name"
        :label="$t('Milestone template name')"
        :show-label="true"
        :classes="'w-full'"
        input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
        name="milestone_template_name"
        type="text"
        :placeholder="$t('Milestone template name')"
        :validate-on-input="true"
        @update:modelValue="
          (e) => {
            template_name = e
          }
        " />
    </div>
    <div class="h-full min-h-[500px] w-full flex gap-2">
      <div class="h-full min-h-[500px] flex flex-col rounded-lg bg-white p-4 w-1/3 min-w-[370px]">
        <div class="flex flex-col has-tooltip">
          <span class="text-[18px] not-italic font-medium font-sans truncate overflow-ellipsis">{{ $t("Milestone template") }}</span>
        </div>
        <div class="flex w-full mt-2 pr-2">
          <div
            class="flex p-2 text-blue font-normal rounded-lg border border-blue cursor-pointer items-center content-center"
            @click="is_add_new_step = true">
            <Plus class="w-5 h-5 ml-1" />
            <div class="pl-1">
              {{ $t("Add new milestone") }}
            </div>
          </div>
        </div>
        <div v-if="is_add_new_step" class="flex flex-col w-full gap-1 mt-2">
          <div class="w-full">
            <TextInput
              :value="new_milestone.name"
              :label="$t('Name your flow')"
              :show-label="true"
              :classes="'w-full'"
              input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
              name="milestone_name"
              type="text"
              :placeholder="$t('Name your flow')"
              :validate-on-input="true"
              @update:modelValue="
                (e) => {
                  new_milestone.name = e
                }
              " />
          </div>
          <div class="flex gap-1 w-full mt-2">
            <div class="relative w-full">
              <label
                for="start_date"
                class="absolute z-10 block text-sm font-medium transition-all ease-in-out -translate-y-1/2 rounded-full h-fit dark:text-dark-search-bar-text dark:text-dark-body-text left-2 duration-side-menu-transition-duration"
                :class="[
                  inputStartDateIsFocused || !!new_milestone.start_date ? 'top-0' : 'top-1/2',
                  isBoxBackgroud
                    ? 'bg-light-box-background dark:bg-dark-box-background'
                    : 'bg-white text-common-grey dark:bg-dark-box-background',
                  inputStartDateIsFocused ? 'text-light-brand-blue dark:text-dark-brand-blue' : ''
                ]">{{ $t("Start date") }}</label>
              <div class="rounded-md shadow-sm">
                <DatePicker
                  v-model="new_milestone.start_date"
                  :is-dark="$store.state.ui.darkMode"
                  class="bg-white dark:bg-dark-screen-background"
                  is-inline
                  :attributes="[
                    {
                      key: 'today',
                      highlight: {
                        color: 'gray',
                        fillMode: 'light'
                      },
                      dates: new Date()
                    }
                  ]"
                  mode="date"
                  :popover="{ visibility: 'click' }">
                  <template #default="{ inputValue, inputEvents }">
                    <div class="relative">
                      <svg
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>

                      <input
                        id="start_date"
                        :value="inputValue"
                        autocomplete="off"
                        class="w-full h-10 px-10 pl-8 pr-2 transition ease-in-out border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
                        :class="[
                          !isBoxBackgroud
                            ? 'bg-light-box-background dark:bg-dark-box-background'
                            : 'bg-light-screen-background dark:bg-dark-box-background'
                        ]"
                        @focusin="focusInputStartDate"
                        @focusout="focusInputStartDate"
                        v-on="inputEvents"
                        @input="
                          $utils.debounce(() => {
                            new_milestone.start_date = $event.target?.value
                          }, 800)
                        ">
                    </div>
                  </template>
                </DatePicker>
              </div>
            </div>
            <div class="relative w-full">
              <label
                for="due_date"
                class="absolute z-10 block text-sm font-medium transition-all ease-in-out -translate-y-1/2 rounded-full h-fit dark:text-dark-search-bar-text dark:text-dark-body-text left-2 duration-side-menu-transition-duration"
                :class="[
                  !!relative_date_text || inputDueDateIsFocused || !!new_milestone.due_date ? 'top-0' : 'top-1/2',
                  isBoxBackgroud
                    ? 'bg-light-box-background dark:bg-dark-box-background'
                    : 'bg-white text-common-grey dark:bg-dark-box-background',
                  inputDueDateIsFocused ? 'text-light-brand-blue dark:text-dark-brand-blue' : ''
                ]">{{ $t("Due date") }}</label>
              <div class="rounded-md shadow-sm">
                <DatePicker
                  v-model="new_milestone.due_date"
                  :is-dark="$store.state.ui.darkMode"
                  class="bg-white dark:bg-dark-screen-background"
                  is-inline
                  :attributes="[
                    {
                      key: 'today',
                      highlight: {
                        color: 'gray',
                        fillMode: 'light'
                      },
                      dates: new Date()
                    }
                  ]"
                  mode="date"
                  :popover="{ visibility: 'none' }">
                  <template #default="{ inputValue, inputEvents }">
                    <div class="relative">
                      <svg
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>

                      <input
                        id="due_date"
                        :value="inputValue ? inputValue : relative_date_text"
                        autocomplete="off"
                        class="w-full h-10 px-10 pl-8 pr-2 transition ease-in-out border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
                        :class="[
                          !isBoxBackgroud
                            ? 'bg-light-box-background dark:bg-dark-box-background'
                            : 'bg-light-screen-background dark:bg-dark-box-background'
                        ]"
                        @focusin="focusInputDueDate"
                        @focusout="focusInputDueDate"
                        @click="is_modal_relative_date_open = true"
                        v-on="inputEvents"
                        @input="
                          $utils.debounce(() => {
                            new_milestone.due_date = $event.target?.value
                          }, 800)
                        ">
                    </div>
                    <div v-if="is_modal_relative_date_open" class="absolute z-20">
                      <DateTimeFrame
                        class="mr-2 w-full bg-white dark:bg-dark-box-background rounded-lg"
                        @done="
                          ($event) => {
                            new_milestone.due_date = $event
                            is_modal_relative_date_open = false
                          }
                        "
                        @relative-date-changed="
                          ($event) => {
                            relative_date = $event
                            is_modal_relative_date_open = false
                          }
                        "
                        @cancel="is_modal_relative_date_open = false" />
                    </div>
                  </template>
                </DatePicker>
              </div>
            </div>
          </div>
          <div class="flex items-center flex-row-reverse justify-between gap-1 w-full mt-2">
            <button
              :disabled="!new_milestone.name || !new_milestone.start_date || (!new_milestone.due_date && !relative_date)"
              class="primary-btn w-fit"
              @click="createNewMilestoneStep()">
              <span>{{ new_milestone?.id ? $t("Update") : $t("Create") }}</span>
              <Check class="w-6 h-6 ml-2" aria-hidden="true" />
            </button>
            <div
              class="text-blue font-medium cursor-pointer"
              @click="
                () => {
                  is_add_new_step = false
                  relative_date = null
                  new_milestone = {
                    id: null,
                    name: null,
                    start_date: null,
                    due_date: null
                  }
                }
              ">
              <span>{{ $t("Cancel") }}</span>
            </div>
          </div>
        </div>
        <div class="flex w-full h-full mt-1 overflow-x-hidden overflow-y-auto">
          <div v-if="milestone?.steps.length == 0 && !is_add_new_step" class="mt-8 flex items-center justify-center w-full h-full">
            <span>{{ $t("No milestones created.") }}</span>
          </div>
          <div v-else-if="milestone?.steps.length > 0 && !is_add_new_step" class="w-full h-full pr-2">
            <div v-for="(step, index) in milestone?.steps" :key="index" class="flex flex-col w-full mb-10">
              <div class="flex w-full ml-2">
                <div
                  v-if="selected_step?.name == step.name"
                  class="absolute left-2 mt-2.5 flex-shrink-0 rounded-full w-2 h-2"
                  :class="[
                    step?.summary?.dynamic_status == 'not_started'
                      ? 'bg-gray-300'
                      : step?.summary?.dynamic_status == 'pending' || step?.summary?.dynamic_status == 'in-progress'
                        ? 'bg-[#9855CD]'
                        : step?.summary?.dynamic_status == 'overdue'
                          ? 'bg-status-warning-yellow'
                          : 'bg-[#20D29F]'
                  ]" />
                <div
                  v-if="step?.summary?.dynamic_status == 'not_started'"
                  class="flex items-center justify-center flex-shrink-0 rounded-full bg-gray-300 w-7 h-7">
                  <span class="rounded-full bg-white w-3 h-3" />
                </div>
                <div
                  v-else-if="step?.summary?.dynamic_status == 'completed'"
                  class="flex items-center justify-center flex-shrink-0 cursor-pointer rounded-full bg-[#20D29F] w-7 h-7"
                  @click="selected_step = step">
                  <Check class="w-5 h-5 text-white font-bold" aria-hidden="true" />
                </div>
                <div
                  v-else-if="step?.summary?.dynamic_status == 'pending' || step?.summary?.dynamic_status == 'in-progress'"
                  class="flex items-center justify-center flex-shrink-0 cursor-pointer rounded-full bg-[#9855CD] w-7 h-7"
                  @click="selected_step = step">
                  <span class="rounded-full bg-white w-3 h-3" />
                </div>
                <div
                  v-else-if="step?.summary?.dynamic_status == 'overdue'"
                  class="flex items-center justify-center flex-shrink-0 cursor-pointer rounded-full bg-status-warning-yellow w-7 h-7"
                  @click="selected_step = step">
                  <span class="rounded-full bg-white w-3 h-3" />
                </div>
                <div class="relative flex items-center justify-between flex-1 w-[calc(100%-200px)] max-w-[calc(100%-200px)]">
                  <div class="flex flex-col flex-shrink-0 ml-1 cursor-pointer w-full" @click="selected_step = step">
                    <span class="has-tooltip flex items-start font-medium w-full">
                      <span class="truncate overflow-ellipsis">{{ step.name }}</span>
                      <span class="tooltip bg-white">{{ step.name }}</span>
                      <Menu as="div" class="flex-shrink-0">
                        <MenuButton
                          class="inline-flex items-center justify-center ease-in-out bg-white rounded dark:bg-dark-box-background duration-side-menu-transition-duration"
                          @click.stop="">
                          <span class="sr-only">{{ $t("Open options") }}</span>
                          <ChevronDown class="mt-1 w-5 h-5" />
                        </MenuButton>
                        <transition
                          enter-active-class="transition duration-100 ease-out"
                          enter-from-class="transform scale-95 opacity-0"
                          enter-to-class="transform scale-100 opacity-100"
                          leave-active-class="transition duration-75 ease-in"
                          leave-from-class="transform scale-100 opacity-100"
                          leave-to-class="transform scale-95 opacity-0">
                          <MenuItems
                            class="absolute z-30 flex flex-col items-start content-center justify-center p-2 mx-3 mt-1 origin-top-right bg-white rounded-md shadow-lg outline-none dark:bg-dark-screen-background ring-1 ring-black ring-opacity-5 focus:outline-none">
                            <MenuItem @click.prevent="editStep(step)">
                              <div class="group flex items-center px-3 py-2 text-sm whitespace-nowrap">
                                <Pencil class="w-5 h-5 mr-4 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                                <span>{{ $t("Edit Milestone") }}</span>
                              </div>
                            </MenuItem>
                            <MenuItem @click.prevent="confirmDeleteStep(step)">
                              <div class="group flex items-center px-3 py-2 text-sm whitespace-nowrap">
                                <Trash2 class="w-5 h-5 mr-4 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                                <span>{{ $t("Delete Milestone") }}</span>
                              </div>
                            </MenuItem>
                          </MenuItems>
                        </transition>
                      </Menu>
                    </span>
                    <span class="text-[14px] not-italic font-normal font-sans leading-normal text-common-grey truncate">{{ step.tasks.length }} tasks - {{ step.goals.length }} goals</span>
                  </div>
                  <div>
                    <ToggleStatusMilestoneButton
                      :disable-toggle="true"
                      :current-status="step?.summary?.dynamic_status"
                      @change="($event) => (step.summary.dynamic_status = $event)" />
                  </div>
                </div>
              </div>
              <div v-if="index !== milestone.steps.length - 1">
                <span class="flex items-start my-1 ml-2">
                  <span class="flex-shrink-0 relative h-6 w-6 flex items-center justify-center">
                    <span class="flex items-start h-20 mt-5 w-1 rounded-lg bg-gray-300" />
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="w-2/3 min-w-[500px] min-h-[500px] h-full rounded-lg bg-white p-4">
        <div v-if="milestone?.steps.length == 0" class="flex flex-col items-center justify-center w-full h-full">
          <div>
            <div class="bg-gray-300 rounded-full p-8">
              <Hourglass width="64" height="64" color="white" />
            </div>
          </div>
          <span class="text-[18px] not-italic font-medium font-sans leading-7 mt-3">{{
            $t("Create a milestone before adding in new steps")
          }}</span>
          <span class="text-[14px] not-italic font-sans text-center text-common-grey leading-5 mt-1">{{
            $t("Click on the bottom at the left to create a new Milestone. After that add as many as activities as you like.")
          }}</span>
        </div>
        <div
          v-else-if="
            selected_step &&
              (selected_step?.summary?.dynamic_status != 'completed' ||
                (selected_step?.summary?.dynamic_status == 'completed' && selected_step?.view_details))
          "
          class="flex flex-col w-full h-full">
          <div class="flex items-center justify-between">
            <div class="flex flex-col">
              <span class="text-[18px] not-italic font-medium font-sans leading-7 mt-3">{{ selected_step.name }}</span>
            </div>
            <div class="flex items-end">
              <div class="flex flex-col">
                <span class="text-[14px] not-italic font-normal font-sans leading-normal text-common-grey">{{ $t("Start Date") }}</span>
                <span class="text-[18px] not-italic font-medium font-sans truncate overflow-ellipsis">{{
                  $moment(selected_step.start_date).format("DD MMM  YYYY")
                }}</span>
              </div>
              <span class="mx-2 text-[18px]"> - </span>
              <div class="flex flex-col">
                <span class="text-[14px] not-italic font-normal font-sans leading-normal text-common-grey">{{ $t("Due Date") }}</span>
                <span class="text-[18px] not-italic font-medium font-sans truncate overflow-ellipsis">{{
                  $moment(selected_step.due_date).format("DD MMM  YYYY")
                }}</span>
              </div>
            </div>
          </div>
          <div class="flex justify-end w-full mt-2 pr-3">
            <Menu v-slot="{ close }" ref="goalsMenu" as="div" class="relative flex-shrink-0 ml-auto">
              <MenuButton class="primary-outline-btn">
                <div class="flex items-center content-center">
                  <Plus class="w-5 h-5 ml-1" />
                  <div class="pl-1">
                    {{ $t("Add new task/goal") }}
                  </div>
                </div>
              </MenuButton>
              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0">
                <MenuItems class="absolute z-50 w-40 mx-3 mt-1 -ml-40 origin-bottom-right bg-transparent border-0 focus:outline-none">
                  <div class="bg-white dark:bg-dark-screen-background rounded-lg w-[340px]">
                    <AsyncAutoCompleteInputGoalsTasks
                      :selected-items="selected_step?.goals?.concat(selected_step?.tasks)"
                      :clear-on-select="true"
                      :is-box-background="true"
                      classes="w-full"
                      input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm "
                      name="goalTaskSearch"
                      value-attr="id"
                      type="text"
                      :placeholder="$t('Search for task/goal to add')"
                      @chosen="
                        ($event) => {
                          handleGoalTaskSelected($event, close)
                        }
                      " />
                  </div>
                </MenuItems>
              </transition>
            </Menu>
          </div>
          <div id="goalstasks" class="flex flex-col w-full h-full mt-4 pt-3 pr-3 overflow-y-auto overflow-x-hidden">
            <div class="text-xl mb-2 font-medium">
              {{ $t("Goals") }}
            </div>

            <draggable
              v-model="selected_step.goals"
              as="div"
              static
              :class="[
                'bg-white dark:border-dark-divider-border border-white dark:bg-dark-box-background py-4 px-2',
                ` gap-x-8 overflow-visible mt-1 border-0 ${
                  $store.state.ui.darkMode ? 'dark-scrollbar' : ''
                } dark:border-dark-divider-border rounded-lg text-base ring-0 overflow-auto focus:outline-none sm:text-sm transition ease-in-out duration-side-menu-transition-duration`
              ]"
              :component-data="{ type: 'transition-group' }"
              v-bind="{
                animation: 200,
                group: 'goals',
                ghostClass: 'ghost',
                chosenClass: 'chosen'
              }"
              item-key="id">
              <template #item="{ element: item }">
                <div class="w-full bg-white cursor-pointer">
                  <div class="w-full">
                    <div
                      v-if="expanded_goals.includes(item.id)"
                      class="relative p-4 mb-4 border rounded-md dark:bg-dark-screen-background dark:border-dark-divider-border dark:text-white"
                      @mouseenter="handleGoalEnter(item)"
                      @mouseleave="handleGoalLeave">
                      <div class="flex items-center">
                        <ToggleStatusButton
                          :prevent-request="true"
                          :object="{ ...item, otype: 'goal' }"
                          @statusChanged="
                            ($event) => {
                              handleStatusChanged($event, item)
                            }
                          " />

                        <div class="flex ml-2">
                          <div>{{ item.name }}</div>
                          <ClipboardCheck class="absolute opacity-0 pointer-events-none" />
                          <Badge :icon="task_icon" :object="item" :list-name="'tasks'" />
                        </div>
                        <div class="absolute flex -right-3 -top-3 gap-x-2">
                          <div
                            class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                            @click="handleExpandGoal(item)">
                            <div class="w-[15px] h-[15px] ml-[5px] mt-[0px] border-b-[3px] border-b-[#45D72D]" />
                            <span
                              class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                              {{ $t("Minimize") }}</span>
                          </div>
                          <div
                            class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:bg-dark-screen-background dark:border-dark-divider-border"
                            @click="handleViewGoal(item)">
                            <Maximize2
                              class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration"
                              color="#2DA7D7" />
                            <span
                              class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                              {{ $t("View Goal") }}</span>
                          </div>
                          <div
                            class="flex items-center justify-center bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                            @click="handleRemoveGoal(item)">
                            <X color="#FF0000" class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" /><span
                              class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                              {{ $t("Remove") }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="flex w-full mt-5">
                        <div class="mt-2 w-[40%]">
                          <div class="flex">
                            <div class="flex gap-x-2">
                              <div v-if="item?.filter" class="text-common-grey">
                                {{ $t("Progress") }}:
                              </div>
                              <div v-else class="text-sm text-common-grey">
                                {{ $t("No filter specified for this goal") }}
                              </div>
                              <div v-if="item?.filter" class="text-sm font-light text-md dark:text-white">
                                {{
                                  item?.record.values[item.filter.split(" ")[1]]
                                    ? $utils.truncateNumber(parseInt(item?.record.values[item.filter.split(" ")[1]]))
                                    : 0
                                }}/{{ $utils.truncateNumber(parseInt(item.filter.split(" ")[item.filter.split(" ").length - 2])) }}
                                {{ $utils.getMetricByReferenceName(item.filter.split(" ")[1])?.name }}
                              </div>
                            </div>
                            <div v-if="item?.record?.values[item.filter?.split(' ')[1]]" class="ml-auto text-lg">
                              {{
                                Math.round(
                                  (parseInt(item?.record.values[item.filter.split(" ")[1]]) /
                                    parseInt(item.filter.split(" ")[item.filter.split(" ").length - 2])) *
                                    100
                                )
                              }}%
                            </div>
                          </div>

                          <div v-if="item.filter" class="py-4 -mt-3 text-sm text-gray-500 whitespace-nowrap">
                            <div class="w-full h-2 bg-gray-300 rounded-full dark:bg-dark-box-background">
                              <div
                                :style="{
                                  width: `${
                                    (item?.record.values[item.filter.split(' ')[1]] ? item?.record.values[item.filter.split(' ')[1]] : 0) /
                                    item.filter.split(' ')[item.filter.split(' ').length - 2] >
                                    1
                                      ? 100
                                      : `${
                                        (item?.record.values[item.filter.split(' ')[1]]
                                          ? item?.record.values[item.filter.split(' ')[1]]
                                          : 0) / item.filter.split(' ')[item.filter.split(' ').length - 2]
                                      }` * 100
                                  }%`
                                }"
                                :class="[` rounded-full bg-blue h-2`]" />
                            </div>
                          </div>
                        </div>
                        <div class="ml-20 w-[10%]">
                          <div class="flex flex-row items-center gap-2">
                            <div class="text-common-grey whitespace-nowrap">
                              {{ $t("Due Date") }}:
                            </div>

                            <div class="flex items-start justify-start w-1/12 py-2 text-start whitespace-nowrap text-md">
                              <span v-if="item.due_date">
                                {{ $moment(item.due_date).format("DD MMM  YYYY") }}
                              </span>

                              <span v-else> - </span>
                            </div>
                          </div>
                        </div>
                        <div :class="[`${item.assigned_to.length > 4 ? 'w-[16%]' : ''}`]" class="ml-auto -mt-8">
                          <div
                            :class="[`${item.assigned_to.length > 4 ? '' : '-ml-10'}`]"
                            class="mb-2 text-common-grey whitespace-nowrap text-start">
                            {{ $t("Team Members") }}
                          </div>
                          <div class="flex items-center -ml-5 -space-x-4 overflow-hidden">
                            <span v-for="user in item.assigned_to.slice(0, 4)" :key="user.id" class="inline-block has-tooltip">
                              <img
                                v-if="user.avatar"
                                class="inline-block rounded-full h-9 w-9 object-cover"
                                :src="user.avatar"
                                onerror="this.onerror=null; this.src=window.default_avatar;"
                                alt="">
                              <Avatar v-else :first-name="user.first_name" :last-name="user.last_name" />
                              <span class="p-1 bg-gray-300 rounded-sm tooltip whitespace-nowrap">
                                <div class="">
                                  <p class="text-sm font-medium dark:!text-white text-gray-700 group-hover:text-gray-900">
                                    {{ Record.from(user).full_name }}
                                  </p>
                                </div>
                              </span>
                            </span>
                            <span
                              v-if="item.assigned_to.length > 4"
                              class="relative ml-auto text-sm left-6 text-light-brand-blue dark:text-dark-brand-blue">
                              + {{ item.assigned_to.length - 4 }} {{ $t("more") }}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      v-else
                      class="relative flex p-4 mb-4 align-middle border rounded-md dark:bg-dark-screen-background dark:border-dark-divider-border dark:text-white"
                      @mouseenter="handleGoalEnter(item)"
                      @mouseleave="handleGoalLeave">
                      <div
                        :class="[
                          'w-4 h-4 rounded-full mt-1 mr-4 relative',
                          {
                            'bg-status-warning-yellow': getActualStatus(item, item?.status) == 'overdue',
                            'bg-dark-brand-blue': ['in-progress', 'pending'].includes(item.status),
                            'bg-status-success-green': item.status == 'completed',
                            'bg-status-error-red': ['cancelled', 'missed'].includes(item.status)
                          }
                        ]" />

                      <div class="flex">
                        <button class="button hover:cursor-move mr-2">
                          <Tally2 class="h-5 w-5 text-common-grey rotate-90" aria-hidden="true" />
                        </button>
                        <div>{{ item.name }}</div>
                        <Badge :icon="task_icon" :object="item" :list-name="'tasks'" />
                      </div>
                      <div v-if="typeof item?.record !== 'string'" class="ml-auto mr-20">
                        <div v-if="item?.record?.values[item.filter?.split(' ')[1]]" class="ml-auto text-md text-common-grey">
                          {{
                            Math.round(
                              (parseInt(item?.record.values[item.filter.split(" ")[1]]) /
                                parseInt(item.filter.split(" ")[item.filter.split(" ").length - 2])) *
                                100
                            )
                          }}%
                        </div>
                      </div>
                      <div class="absolute flex -right-3 -top-3 gap-x-2">
                        <div
                          class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                          @click="handleExpandGoal(item)">
                          <div class="w-[15px] h-[15px] ml-[5px] mt-[5px] border-[3px] rounded-md border-[#45D72D]" />
                          <span
                            class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                            {{ $t("Expand") }}</span>
                        </div>
                        <div
                          class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:bg-dark-screen-background dark:border-dark-divider-border"
                          @click="handleViewGoal(item)">
                          <Maximize2
                            class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration"
                            color="#2DA7D7" />
                          <span
                            class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                            {{ $t("View Goal") }}</span>
                        </div>
                        <div
                          class="flex items-center justify-center bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                          @click="handleRemoveGoal(item)">
                          <X color="#FF0000" class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" /><span
                            class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                            {{ $t("Remove") }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </draggable>

            <div class="text-xl font-medium my-2">
              {{ $t("Tasks") }}
            </div>

            <draggable
              v-model="selected_step.tasks"
              as="div"
              static
              :class="[
                'bg-white dark:border-dark-divider-border border-white dark:bg-dark-box-background py-4 px-2',
                ` gap-x-8 overflow-visible mt-1 border-0 ${
                  $store.state.ui.darkMode ? 'dark-scrollbar' : ''
                } dark:border-dark-divider-border rounded-lg text-base ring-0 overflow-auto focus:outline-none sm:text-sm transition ease-in-out duration-side-menu-transition-duration`
              ]"
              :component-data="{ type: 'transition-group' }"
              v-bind="{
                animation: 200,
                group: 'tasks',
                ghostClass: 'ghost',
                chosenClass: 'chosen'
              }"
              item-key="id">
              <template #item="{ element: item }">
                <div class="w-full bg-white cursor-pointer">
                  <div class="w-full">
                    <div
                      v-if="expanded_tasks.includes(item.id)"
                      class="relative p-4 mb-4 border rounded-md dark:bg-dark-screen-background dark:border-dark-divider-border dark:text-white"
                      @mouseenter="handleTaskEnter(item)"
                      @mouseleave="handleTaskLeave">
                      <div class="flex items-center">
                        <ToggleStatusButton
                          :prevent-request="true"
                          :object="{ ...item, otype: 'task' }"
                          @statusChanged="
                            ($event) => {
                              handleStatusChanged($event, item)
                            }
                          " />
                        <div class="ml-2">
                          {{ item.name }}
                        </div>
                        <div class="absolute flex -right-3 -top-3 gap-x-2">
                          <div
                            class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                            @click="handleExpandTask(item)">
                            <div class="w-[15px] h-[15px] ml-[5px] mt-[0px] border-b-[3px] border-b-[#45D72D]" />
                            <span
                              class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                              {{ $t("Minimize") }}</span>
                          </div>
                          <div
                            class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:bg-dark-screen-background dark:border-dark-divider-border"
                            @click="handleViewTask(item)">
                            <Maximize2
                              class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration"
                              color="#2DA7D7" />
                            <span
                              class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                              {{ $t("View Task") }}</span>
                          </div>
                          <div
                            class="flex items-center justify-center bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                            @click="handleRemoveTask(item)">
                            <X color="#FF0000" class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" /><span
                              class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                              {{ $t("Remove") }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="flex w-full mt-5">
                        <div class="w-1/3">
                          <div class="flex flex-row items-center gap-2">
                            <div class="text-common-grey">
                              {{ $t("Priority") }}:
                            </div>

                            <div class="flex items-start justify-start w-1/12 py-2 text-start whitespace-nowrap text-md">
                              <div v-if="item.priority" class="flex items-center capitalize gap-x-2">
                                <div>
                                  {{ item.priority }}
                                </div>
                                <div>
                                  <div v-if="item.priority == 'normal'">
                                    <div :class="[`border-b-2 w-3  border-status-warning-yellow`]" />
                                    <div :class="[`border-b-2 w-3 mt-1 border-status-warning-yellow`]" />
                                  </div>
                                  <div v-else-if="item.priority == 'high'">
                                    <ChevronRight class="w-5 h-5 -rotate-90 text-status-error-red" />
                                  </div>
                                  <div v-else-if="item.priority == 'highest'">
                                    <ChevronsRight class="w-5 h-5 -rotate-90 text-status-error-red" />
                                  </div>
                                  <div v-else-if="item.priority == 'low'">
                                    <ChevronRight class="w-5 h-5 rotate-90 text-status-success-green" />
                                  </div>
                                  <div v-else-if="item.priority == 'lowest'">
                                    <ChevronsRight class="w-5 h-5 rotate-90 text-status-success-green" />
                                  </div>
                                </div>
                              </div>

                              <span v-else> - </span>
                            </div>
                          </div>
                        </div>

                        <div class="w-1/3">
                          <div class="flex flex-row items-center gap-2">
                            <div class="text-common-grey whitespace-nowrap">
                              {{ $t("Due Date") }}:
                            </div>

                            <div class="flex items-start justify-start w-1/12 py-2 text-start whitespace-nowrap text-md">
                              <span v-if="item.due_date">
                                {{ $moment(item.due_date).format("DD MMM  YYYY") }}
                              </span>

                              <span v-else> - </span>
                            </div>
                          </div>
                        </div>

                        <div :class="[`${item.assigned_to.length > 4 ? 'w-[16%]' : ''}`]" class="ml-auto -mt-8">
                          <div
                            :class="[`${item.assigned_to.length > 4 ? '' : '-ml-10'}`]"
                            class="mb-2 text-common-grey whitespace-nowrap text-start">
                            {{ $t("Team Members") }}
                          </div>
                          <div class="flex items-center -ml-5 -space-x-4 overflow-hidden">
                            <span v-for="user in item.assigned_to.slice(0, 4)" :key="user" class="inline-block has-tooltip">
                              <img
                                v-if="$utils.getUserById(user, 'avatar')"
                                class="inline-block rounded-full h-9 w-9 object-cover"
                                :src="$utils.getUserById(user, 'avatar')"
                                alt="">
                              <Avatar
                                v-else
                                :first-name="$utils.getUserById(user, 'first_name')"
                                :last-name="$utils.getUserById(user, 'last_name')" />
                              <span class="p-1 bg-gray-300 rounded-sm tooltip whitespace-nowrap">
                                <div class="">
                                  <p class="text-sm font-medium dark:!text-white text-gray-700 group-hover:text-gray-900">
                                    {{ $utils.getUserById(user, "full_name") }}
                                  </p>
                                </div>
                              </span>
                            </span>
                            <span
                              v-if="item.assigned_to.length > 4"
                              class="relative ml-auto text-sm left-6 text-light-brand-blue dark:text-dark-brand-blue">
                              + {{ item.assigned_to.length - 4 }} {{ $t("more") }}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      v-else
                      class="relative flex p-4 mb-4 align-middle border rounded-md dark:bg-dark-screen-background dark:border-dark-divider-border dark:text-white"
                      @mouseenter="handleTaskEnter(item)"
                      @mouseleave="handleTaskLeave">
                      <div
                        :class="[
                          'w-4 h-4 rounded-full mt-1 mr-4 relative',
                          {
                            'bg-status-warning-yellow': getActualStatus(item, item.status) == 'overdue',
                            'bg-dark-brand-blue': ['in-progress', 'pending'].includes(item.status),

                            'bg-status-success-green': item.status == 'completed',
                            'bg-status-error-red': ['cancelled', 'missed'].includes(item.status)
                          }
                        ]" />

                      <button class="button hover:cursor-move mr-2">
                        <Tally2 class="h-5 w-5 text-common-grey rotate-90" aria-hidden="true" />
                      </button>

                      <div>{{ item.name }}</div>

                      <div class="absolute flex -right-3 -top-3 gap-x-2">
                        <div
                          class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                          @click="handleExpandTask(item)">
                          <div class="w-[15px] h-[15px] ml-[5px] mt-[5px] border-[3px] rounded-md border-[#45D72D]" />
                          <span
                            class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                            {{ $t("Expand") }}</span>
                        </div>
                        <div
                          class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:bg-dark-screen-background dark:border-dark-divider-border"
                          @click="handleViewTask(item)">
                          <Maximize2
                            class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration"
                            color="#2DA7D7" />
                          <span
                            class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                            {{ $t("View Task") }}</span>
                        </div>
                        <div
                          class="flex items-center justify-center bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                          @click="handleRemoveTask(item)">
                          <X color="#FF0000" class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" /><span
                            class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                            {{ $t("Remove") }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </draggable>
          </div>
        </div>
        <div
          v-else-if="selected_step && selected_step?.summary?.dynamic_status == 'completed' && !selected_step?.view_details"
          class="flex flex-col w-full h-full">
          <div class="flex flex-col items-end w-full">
            <span class="text-[15px] text-common-grey not-italic font-medium font-sans leading-7">Completed on</span>
            <span class="text-[18px] not-italic font-medium font-sans leading-7">{{ $moment(new Date()).format("DD MMM  YYYY") }}</span>
          </div>
          <div class="flex flex-col items-center justify-center w-full h-full">
            <div>
              <div class="bg-[#20D29F] rounded-full p-8">
                <Check class="w-16 h-16 text-white" aria-hidden="true" />
              </div>
            </div>
            <span class="text-[18px] not-italic font-medium font-sans leading-7 mt-3">{{ $t("Milestone completed successfully") }}</span>
            <span class="text-[14px] not-italic font-sans text-common-grey leading-5 mt-1">{{
              $t("Congratulations! You have completed all steps in this milestone.")
            }}</span>
            <span class="text-[14px] not-italic font-sans text-common-grey leading-5 mt-1"><span class="text-blue cursor-pointer" @click="selected_step['view_details'] = true">{{ $t("Click here") }}</span>
              {{ $t("to see which goals and tasks were part of this milestone.") }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-row justify-end gap-2 mt-5 text-center">
      <button type="button" class="cancel-btn" @click="$emit('cancel')">Cancel</button>
      <button :disabled="!template_name" type="button" class="primary-btn w-fit" @click="saveTemplate()">
        {{ editTemplate ? "Update template" : "Save Milestone as template" }}
      </button>
    </div>
    <Modal v-model:is-open="is_show_goals_tasks_modal_open" default-success-msg="" :use-toast="true">
      <template #form="{ close: closeModal }">
        <div id="goalstasks2" class="mt-5">
          <!--get content by id -->
        </div>
        <div class="flex justify-end w-full">
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 mt-5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm group dark:text-dark-body-text dark:bg-dark-box-background dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none"
            @click="closeModal">
            {{ $t("Close") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Add notes to step -->
    <Modal v-model:is-open="is_add_notes_modal_open" :use-toast="true" :default-success-msg="$t('Notes saved')">
      <template #form="{ done: showIsDone, close: closeModal }">
        <MilestoneStepNotes :step="selected_step" @done="showIsDone" @cancel="closeModal" />
      </template>
    </Modal>

    <!-- Delete step -->
    <Modal
      v-model:is-open="remove_step_modal_is_open"
      :default-success-msg="$t('Milestone removed successfully')"
      max-width="max-w-[600px] mx-5">
      <template #form="{ close: closeModal }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Remove milestone") }}
        </h3>
        <div class="max-w-xl mt-2 text-gray-500 text-md">
          <p>{{ $t("Do you want to remove this milestone?") }}</p>
        </div>
        <div class="flex flex-row justify-end gap-2 mt-5 text-center">
          <button type="button" class="cancel-btn" @click="closeModal">{{ $t("Cancel") }}</button>
          <button type="button" class="danger-btn w-fit" @click="removeStep(closeModal)">{{ $t("Delete") }}</button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import draggable from "vuedraggable"

import dataService from "@/services/data.service"
import {
  Hourglass,
  Maximize2,
  ClipboardCheck,
  X,
  ChevronDown,
  ChevronsRight,
  ChevronRight,
  Tally2,
  Check,
  Plus,
  Trash2,
  Pencil
} from "lucide-vue-next"
import Badge from "@/components/shared/ActivityStatusBadge.vue"
import Avatar from "@/components/shared/Avatar.vue"
import TextInput from "@/components/shared/TextInput.vue"
import ToggleStatusMilestoneButton from "@/components/shared/ToggleStatusMilestoneButton.vue"
import ToggleStatusButton from "@/components/shared/ToggleStatusButton.vue"
import DateTimeFrame from "@/components/shared/DateTimeFrame.vue"
import { DatePicker } from "v-calendar"
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/vue"
import AsyncAutoCompleteInputGoalsTasks from "@/components/shared/AsyncAutoCompleteInputGoalsTasks.vue"
import MilestoneStepNotes from "@/components/shared/MilestoneStepNotes.vue"
import { Record } from "@/models/record"
import Modal from "@/components/shared/Modal.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    Tally2,
    draggable,
    Modal,
    MilestoneStepNotes,
    AsyncAutoCompleteInputGoalsTasks,
    Menu,
    MenuButton,
    MenuItems,
    MenuItem,
    Avatar,
    Pencil,
    Trash2,
    ChevronRight,
    ChevronDown,
    ChevronsRight,
    X,
    Maximize2,
    Badge,
    ClipboardCheck,
    Plus,
    Check,
    Hourglass,
    TextInput,
    DatePicker,
    DateTimeFrame,
    ToggleStatusButton,
    ToggleStatusMilestoneButton
  },
  props: {
    milestoneId: {
      type: String,
      default: ""
    },
    editTemplate: {
      type: Boolean,
      default: false
    },
    template: {
      type: Object,
      default: null
    }
  },

  emits: ["cancel", "done"],
  data() {
    return {
      template_name: "",
      remove_step_modal_is_open: false,
      step_to_be_deleted: null,
      is_add_notes_modal_open: false,
      is_show_goals_tasks_modal_open: false,
      is_modal_relative_date_open: false,
      Record,
      task_icon: ClipboardCheck,
      isBoxBackgroud: false,
      inputStartDateIsFocused: false,
      inputDueDateIsFocused: false,
      is_add_new_step: false,
      relative_date: null,
      relative_date_text: null,
      new_milestone: {
        id: null,
        name: "",
        start_date: null,
        due_date: null,
        status: "not_started",
        tasks: [],
        goals: []
      },
      milestone: {
        steps: []
      },
      selected_step: null,
      selected_goals_tasks: [],
      expanded_goals: [],
      expanded_tasks: [],
      goal_hover_id: null,
      task_hover_id: null
    }
  },
  watch: {
    relative_date() {
      if (this.relative_date) {
        if (this.relative_date.due_date_relative_to == "datetime_created") {
          this.relative_date_text = `${this.relative_date.due_date_days_offset} ` + this.$t("days after Creation Date")
        } else if (this.relative_date.due_date_relative_to == "start_date") {
          this.relative_date_text = `${this.relative_date.due_date_days_offset} ` + this.$t("days after Start Date")
        } else {
          this.relative_date_text = `${this.relative_date.due_date_days_offset} ` + this.$t("days from In Progress")
        }
        this.new_milestone.due_date = null
      }
    },
    "new_milestone.due_date": {
      deep: true,
      handler() {
        if (this.new_milestone.due_date) {
          this.relative_date_text = null
        }
      }
    }
  },
  created() {
    if (this.editTemplate) {
      this.template_name = this.template.name
      if (this.template?.content?.steps) {
        this.template.content.steps.map((step) => {
          step["summary"] = {
            dynamic_status: step.status
          }
        })
        this.milestone.steps = this.template.content.steps

        this.milestone.steps.forEach((step) => {
          step.goals.forEach((goal) => {
            goal["id"] = this.$uuid()
          })
          step.tasks.forEach((task) => {
            task["id"] = this.$uuid()
          })
        })
        this.selected_step = this.template.content.steps[0]
      }
    }
    this.$emitter.on(ALL_EMITTERS.ADD_TASK_TO_MILESTONE_CREATE_FORM, (task) => {
      this.handleGoalTaskSelected(task)
    })
    this.$emitter.on(ALL_EMITTERS.ADD_GOAL_TO_MILESTONE_CREATE_FORM, (goal) => {
      this.handleGoalTaskSelected(goal)
    })
  },
  methods: {
    getActualStatus(object, status) {
      if (object.due_date) {
        if (
          (status == "pending" || status == "in-progress") &&
          this.$moment(object.due_date).local().startOf("day").isBefore(this.$moment().startOf("day"))
        ) {
          return "overdue"
        }
        return status
      }
      return status
    },
    handleStatusChanged(status, item) {
      item.status = status
    },
    saveTemplate() {
      let data = {}
      data["name"] = this.template_name
      data["source_object_type"] = "milestone"
      data["source_object_id"] = null
      let content = {}
      content["name"] = this.template_name
      content["source_object_type"] = "milestone"
      content["source_object_id"] = null
      content["organization"] = this.$store.state.organization.id
      content["start_date"] = null
      content["due_date"] = null
      content["description"] = null
      content["notes"] = []
      content["description"] = null
      content["teams"] = []
      content["companies"] = []
      content["contacts"] = []
      content["collaborators"] = [this.$store.state.auth.user.id]
      content["tags"] = []
      content["steps"] = []
      this.milestone.steps.map((step) => {
        let step_data = {}
        step_data["organization"] = this.$store.state.organization.id
        step_data["attachments"] = []
        step_data["auto_start_date_set"] = false
        step_data["in_progress_date"] = null
        step_data["completed_date"] = null
        step_data["name"] = step.name
        step_data["start_date"] = step.start_date
        step_data["due_date"] = step.due_date
        step_data["description"] = null
        step_data["notes"] = []
        step_data["teams"] = []
        step_data["companies"] = []
        step_data["contacts"] = []
        step_data["collaborators"] = [this.$store.state.auth.user.id]
        step_data["tags"] = []

        step_data["due_date_relative_to"] = this.relative_date?.due_date_relative_to
        step_data["due_date_days_offset"] = this.relative_date?.due_date_days_offset
        step_data["status"] = "pending"
        step_data["show_dynamic_status"] = true
        //step_data["created_by"] = this.$store.state.user.id

        let goals = []
        step.goals.map((goal) => {
          goal["attachments"] = []
          goal["record"] = null
          goal["contacts"] = []
          goal["assigned_to"] = [this.$store.state.auth.user.id]
          goal["notes"] = []
          goal["tasks"] = []

          if (goal.tasks.results?.length) {
            goal.tasks.results?.map((task) => {
              task["attachments"] = []
              task["record"] = null
              task["contacts"] = []
              task["assigned_to"] = [this.$store.state.auth.user.id]
              task["notes"] = []
              goal["tasks"].push(task)
            })
          }
          goals.push(goal)
        })
        step_data["goals"] = goals

        let tasks = []
        step.tasks.map((task) => {
          task["attachments"] = []
          task["record"] = null
          task["contacts"] = []
          task["assigned_to"] = [this.$store.state.auth.user.id]
          task["notes"] = []
          tasks.push(task)
        })

        step_data["tasks"] = tasks

        content["steps"].push(step_data)
      })
      data["content"] = content

      if (this.editTemplate) {
        let data_template = { ...this.template.content }
        data_template["steps"] = this.milestone.steps
        dataService.updateTemplate(this.template.id, data).then((res) => {
          this.$emitter.emit(ALL_EMITTERS.TEMPLATE_UPDATED, res.data)
          this.$emit("done")
        })
      } else {
        dataService.createTemplate(data).then((res) => {
          this.$emitter.emit(ALL_EMITTERS.NEW_ACTIVITY_TEMPLATE_ADDED, res.data)
          this.$emit("done")
          this.$toast.open({
            message: this.$t("Template created successfully."),
            position: "top",
            type: "success",
            duration: 3000
          })
        })
      }
    },
    createNewMilestoneStep() {
      let data = {}
      data["name"] = this.new_milestone["name"]
      data["start_date"] = this.new_milestone["start_date"]
      data["due_date"] = this.new_milestone["due_date"]
      data["summary"] = { dynamic_status: "pending" }
      data["goals"] = []
      data["tasks"] = []
      data["due_date_relative_to"] = this.relative_date?.due_date_relative_to
      data["due_date_days_offset"] = this.relative_date?.due_date_days_offset
      this.milestone.steps.push(data)
      this.selected_step = data
      this.is_add_new_step = false
      this.relative_date = null
      this.new_milestone = {
        id: null,
        name: "",
        start_date: null,
        due_date: null
      }
    },
    editStep(step) {
      this.new_milestone = this.$utils.clone(step)
      this.is_add_new_step = true
    },
    confirmDeleteStep(step) {
      this.step_to_be_deleted = step
      this.remove_step_modal_is_open = true
    },
    removeStep(closeModal) {
      dataService
        .removeMilestoneStep(this.milestoneId, {
          steps_ids: [this.step_to_be_deleted.id]
        })
        .then(() => {
          closeModal()
        })
    },
    handleGoalTaskSelected(item, close) {
      if (item.otype == "goal") {
        this.selected_step["goals"].push(item)
      } else if (item.otype == "task") {
        this.selected_step["tasks"].push(item)
      }
      this.selected_goals_tasks.push(item)
      close()
    },
    handleGoalEnter(goal) {
      this.$utils.debounce(() => {
        this.goal_hover_id = goal.id
      }, 600)
    },
    handleGoalLeave() {
      this.goal_hover_id = null
      this.$utils.debounce(() => {
        this.goal_hover_id = null
      }, 600)
    },
    handleTaskEnter(task) {
      this.$utils.debounce(() => {
        this.task_hover_id = task.id
      }, 600)
    },
    handleTaskLeave() {
      this.task_hover_id = null
      this.$utils.debounce(() => {
        this.task_hover_id = null
      }, 600)
    },
    handleViewGoal(goal) {
      this.$emitter.emit(ALL_EMITTERS.UPDATE_GOAL, { goal: this.$utils.clone(goal) })
    },
    handleViewTask(task) {
      this.$emitter.emit(ALL_EMITTERS.UPDATE_TASK, { task: this.$utils.clone(task) })
    },
    handleExpandGoal(goal) {
      let index = this.expanded_goals.findIndex((g) => g == goal.id)
      if (index !== -1) {
        this.expanded_goals.splice(index, 1)
      } else {
        this.expanded_goals.push(goal.id)
      }
    },

    handleExpandTask(task) {
      let index = this.expanded_tasks.findIndex((t) => t == task.id)
      if (index !== -1) {
        this.expanded_tasks.splice(index, 1)
      } else {
        this.expanded_tasks.push(task.id)
      }
    },
    handleRemoveGoal(goal) {
      this.selected_step["goals"] = this.selected_step["goals"].filter((g) => g.id !== goal.id)
    },
    handleRemoveTask(task) {
      this.selected_step["tasks"] = this.selected_step["tasks"].filter((g) => g.id !== task.id)
    },
    focusInputStartDate() {
      this.inputStartDateIsFocused = !this.inputStartDateIsFocused
    },
    focusInputDueDate() {
      this.inputDueDateIsFocused = !this.inputDueDateIsFocused
    }
  }
}
</script>
